import React, { memo, useMemo, useCallback, forwardRef } from 'react';
import cx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { PreviewCardContainer } from '@debank/content-editor';

import { useDeviceTypeIsMobile, useUrl } from '@/hooks';
import { useImagePreview } from '@/hooks/useImagePreview';
import type { FeedArticle } from '@/views/Stream';
import { withHttp } from '@/utils';

import { LazyMediaSized } from '../LazyMedia';
import { omitRefCode } from '../ReferralCodeBtn/utils';
import { ArticleEntityContent } from './Content';
import { DrawCard } from './DrawCard';
import { PollCard } from './PollCard';
import { BotAlertCard } from './BotAlertCard';
import { QaCard } from './QaCard';
import { DowngradeCard, InvisibleCard } from './DowngradeCard';
import style from './index.module.scss';
import { ThirdPartyAppCard } from './ThirdPartyAppCard';
import { ProposalCard } from './ProposalCard';
import { QuestCard } from './QuestCard';

type RichTextViewProps = {
  className?: string;
  article: FeedArticle;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  maxDisplay?: number;
  showRawImage?: boolean;
  maxImgSize?: [number, number];
  enableTranslate?: boolean;
  isDetails?: boolean;
  contentClassName?: string;
  imageClassName?: string;
  previewCardQuoted?: boolean;
};

const MAX_IMG_WIDTH = 507;
const MAX_IMG_HEIGHT = 510;

export const SUPPORTED_ARTICLE_TYPE = [
  'text',
  'draw',
  'poll',
  'thread',
  'pay_thread',
  'monitor_alert',
  'pay_qa',
  'template',
  'data_proposal',
  'quest',
  'quote',
];

const HIDE_ARTICLE_CONTENT = ['pay_qa'];

export const RichTextView = memo(
  forwardRef((props: RichTextViewProps, ref: React.Ref<HTMLDivElement>) => {
    const {
      className,
      article,
      onClick,
      maxDisplay,
      showRawImage,
      maxImgSize,
      // enableTranslate,
      isDetails,
      contentClassName,
      imageClassName,
      previewCardQuoted,
    } = props;
    const isMobile = useDeviceTypeIsMobile();
    const { handlePreview } = useImagePreview();
    const isImageShown = useMemo(() => !!article.images?.length, [article]);
    const isCardShown = useMemo(
      () => !isImageShown && article.card?.url && !article.draw && !article.poll,
      [isImageShown, article],
    );
    const { checkAndOpenUrl } = useUrl();
    const [maxImgWidth, maxImgHeight] = maxImgSize || [MAX_IMG_WIDTH, MAX_IMG_HEIGHT];

    const handleContentClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onClick?.(e);
      },
      [onClick],
    );

    const handleImageClick = useCallback(
      (e: React.MouseEvent<HTMLImageElement>) => {
        handlePreview(e);
        e.stopPropagation();
      },
      [handlePreview],
    );

    const handleOpenUrl = useCallback(
      (url: string) => {
        checkAndOpenUrl(omitRefCode(withHttp(url)));
      },
      [checkAndOpenUrl],
    );

    const articleImages = useMemo(() => {
      return article.images?.map(x => {
        return showRawImage ? x.raw : x.resize;
      });
    }, [article.images, showRawImage]);

    const isDowngrade = useMemo(
      // article?.type 为空是 threads
      () => article?.type && SUPPORTED_ARTICLE_TYPE.indexOf(article?.type) === -1,
      [article?.type],
    );

    if (!article.is_visible) {
      return (
        <InvisibleCard
          className={className}
          ref={ref}
          article={article}
          handleContentClick={handleContentClick}
        />
      );
    }

    if (isDowngrade) {
      return (
        <DowngradeCard className={className} ref={ref} handleContentClick={handleContentClick} />
      );
    }

    return (
      <div className={cx(style.articleMain, className)} ref={ref}>
        {HIDE_ARTICLE_CONTENT.includes(article.type) ? null : (
          <div className={cx(style.articleContent, contentClassName)} onClick={handleContentClick}>
            <ArticleEntityContent
              article={article}
              maxDisplay={maxDisplay}
              openUrl={handleOpenUrl}
              contentType="post"
            />
          </div>
        )}
        {article.pay_qa ? (
          <QaCard article={article} isDetails={isDetails} maxDisplay={maxDisplay} />
        ) : null}
        {isImageShown ? (
          // 现在 images 没有点击放大，点击直接到 详情页
          <div
            className={cx(
              style.images,
              !article?.content && style.isOnlyImg,
              isMobile && style.isMobile,
            )}
            onClick={handleContentClick}>
            {articleImages?.map((x, i) => (
              // app 这里用 resize.full_url
              <LazyMediaSized
                className={cx(style.image, imageClassName)}
                src={x.full_url}
                onClick={handleImageClick}
                key={i}
                maxHeight={maxImgHeight}
                maxWidth={maxImgWidth}
                height={x.height}
                width={x.width}
              />
            ))}
          </div>
        ) : null}
        {article.monitor_alert ? <BotAlertCard article={article} /> : null}
        {article.draw ? (
          <div className={style.drawCardWrapper}>
            <DrawCard article={article} />
          </div>
        ) : null}
        {article.poll ? (
          <div className={style.pollCardWrapper}>
            <PollCard article={article} />
          </div>
        ) : null}
        {article.template ? (
          <div className={style.pollCardWrapper}>
            <ThirdPartyAppCard article={article} />
          </div>
        ) : null}
        {article?.proposal ? (
          <div className={style.pollCardWrapper}>
            <ProposalCard article={article} />
          </div>
        ) : null}
        {article?.quest ? (
          <div className={style.pollCardWrapper}>
            <QuestCard article={article} isDetails={isDetails} />
          </div>
        ) : null}
        {isCardShown ? (
          <div className={style.card}>
            <PreviewCardContainer
              previewCard={previewCardQuoted ? { ...article.card, type: 'summary' } : article.card}
              onOpenUrl={handleOpenUrl}
              enableNavigation
            />
          </div>
        ) : null}
      </div>
    );
  }),
);

export const ImageSkeleton = memo(() => {
  return <Skeleton width="100%" height={260} borderRadius={12} />;
});

export * from './Content';
